export interface ShouldPromptForFormEditorLeaveInput {
	isDirty: boolean;
	nextPathname: string;
	formBaseHref: string;
}

/**
 * True when a navigation should ask before leaving the Form Editor.
 * Builder and Rules stay on the editor base path and do not prompt.
 */
export function shouldPromptForFormEditorLeave({
	isDirty,
	nextPathname,
	formBaseHref,
}: ShouldPromptForFormEditorLeaveInput): boolean {
	if (!isDirty) return false;
	return !isInsideFormEditor(nextPathname, formBaseHref);
}

export function isInsideFormEditor(
	pathname: string,
	formBaseHref: string,
): boolean {
	const base = formBaseHref.replace(/\/$/, "");
	return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * Drawer Form (`/schema`) and page Form (`/full?tab=form` or `/full/form`) are one editor.
 * Moving between them keeps unsaved edits and does not prompt.
 */
export function isSiblingEntityTypeFormSurface(
	next: { pathname: string; search: unknown },
	detailHref: string,
): boolean {
	const typeBase = detailHref.replace(/\/full$/, "").replace(/\/$/, "");
	const path = next.pathname.replace(/\/$/, "");
	if (path === `${typeBase}/schema` || path.startsWith(`${typeBase}/schema/`)) {
		return true;
	}
	const onFull =
		path === `${typeBase}/full` || path.startsWith(`${typeBase}/full/`);
	if (!onFull) return false;
	if (
		path === `${typeBase}/full/form` ||
		path.startsWith(`${typeBase}/full/form/`)
	) {
		return true;
	}
	const tab = (next.search as { tab?: string } | null)?.tab;
	return tab === "form";
}

export type FormEditorLeaveChoice = "stay" | "discard" | "apply";

export interface FormEditorLeaveActions {
	revert: () => void;
	save: () => Promise<boolean>;
	proceed: () => void;
	reset: () => void;
}

/**
 * Stay keeps the blocked navigation. Discard reverts, then continues.
 * Apply saves, then continues. A failed save stays put.
 */
export async function resolveFormEditorLeave(
	choice: FormEditorLeaveChoice,
	actions: FormEditorLeaveActions,
): Promise<void> {
	if (choice === "stay") {
		actions.reset();
		return;
	}
	if (choice === "discard") {
		actions.revert();
		actions.proceed();
		return;
	}
	const saved = await actions.save();
	if (!saved) {
		actions.reset();
		return;
	}
	actions.proceed();
}
